import { useState, useEffect } from "react";
import { createAppointmentAndSubmitPrescription } from "../services/apiServices";
import Api from "../Api/Api.json";
import {
  FileText,
  Calendar,
  TestTube,
  Eye,
  Save,
  X,
  Plus,
} from "lucide-react";

import EnhancedTestSelector from "./EnhancedTestSelector";
import EnhancedMedicineTable from "./EnhancedMedicines";
import TestResultsTable from "./TestResult";

import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import { usePatientInfoFromPhone } from "../hooks/usePatientInfoFromPhone";
import { useAppointments } from "../hooks/useAppointments";
import MedicineSelector from "./MedicineSelector";
import MedicineResults from "./MedicineResults";
import Copilot from "./Copilot";
import PhotoPrescriptionPreview from "../pages/PhotoPrescriptionPreview";
import PhotoPrescriptionPreviewUpdate8Oct25 from "../pages/PhotoPrescriptionPreviewUpdate8Oct25";

pdfMake.vfs = pdfFonts.vfs;

export default function EnhancedPrescriptionForm({ patientPhone }) {
  const [appointmentId, setAppointmentId] = useState("");
  // Text fields
  const [complaints, setComplaints] = useState("");
  const [onExam, setOnExam] = useState("");
  const [comments, setComments] = useState("");
  const [advices, setAdvices] = useState("");
  // Pathology and Radiology Tests and Medicines
  const [pathologyList, setPathologyList] = useState([]);
  const [radiologyList, setRadiologyList] = useState([]);
  const [medicineList, setMedicineList] = useState([]);
  // Selected tests and medicines
  const [selectedPathology, setSelectedPathology] = useState([]);
  const [selectedRadiology, setSelectedRadiology] = useState([]);
  const [medicines, setMedicines] = useState([]);
  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [doctorName, setDoctorName] = useState(null);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  
  // New Medicine Dialog states
  const [showNewMedicineDialog, setShowNewMedicineDialog] = useState(false);
  const [medicineUrl, setMedicineUrl] = useState("");
  const [medicineData, setMedicineData] = useState(null);
  const [isLoadingMedicine, setIsLoadingMedicine] = useState(false);

  // QA Dialog states
  const [showQADialog, setShowQADialog] = useState(false);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState("");
  const [qaResult, setQaResult] = useState(null);
  
  // query states
  const { data: patientInfo, isLoading: patientInfoLoading, isError: isPatientInfoError, error: patientInfoError } = usePatientInfoFromPhone(patientPhone);
  const { data: appointments, isLoading: appointmentsLoading, isError: isAppointmentsError, error: appointmentsError} = useAppointments(patientInfo?.id, setAppointmentId);
  useEffect(() => {
    fetch("/pathologyTests.json")
      .then((res) => res.json())
      .then((data) => setPathologyList(data))
      .catch((err) => console.error("Error loading pathology tests:", err));

    // Load radiology tests
    fetch("/radiologyTests.json")
      .then((res) => res.json())
      .then((data) => setRadiologyList(data))
      .catch((err) => console.error("Error loading radiology tests:", err));

    // Load all medicines
    fetch("/allMedicines_dgda.json")
      .then((res) => res.json())
      .then((data) => setMedicineList(data))
      .catch((err) => console.error("Error loading medicines:", err));

      //get doctor id
      const userInfo = JSON.parse(localStorage.getItem("user_info"));
      console.log(userInfo);
      setDoctorId(userInfo.id);
      setDoctorName(userInfo.name);
      console.log(userInfo.id);
  }, []);

  if (patientInfoLoading || appointmentsLoading) {
    return <div className="text-center text-gray-600">Loading...</div>;
  }

  if (isPatientInfoError || isAppointmentsError) {
    return (
      <div className="text-center text-red-600">
        Error loading data:{" "}
        {patientInfoError?.message ||
          appointmentsError?.message}
      </div>
    );
  };

  const handleAddPatho = (test) => {
    setSelectedPathology([...selectedPathology, test]);
  };
  const handleAddRadio = (test) => {
    setSelectedRadiology([...selectedRadiology, test]);
  };

  const handleRemovePatho = (idx) => {
    setSelectedPathology(selectedPathology.filter((_, i) => i !== idx));
  };
  const handleRemoveRadio = (idx) => {
    setSelectedRadiology(selectedRadiology.filter((_, i) => i !== idx));
  };
  const handleAddMedicine = (med) => {
    setMedicines([...medicines, med]);
    console.log(medicines);
  };
  const handleRemoveMedicine = (idx) => {
    setMedicines(medicines.filter((_, i) => i !== idx));
  };

  const handleSubmitMedicineUrl = async () => {
    if (!medicineUrl.trim()) {
      alert("Please enter a valid URL");
      return;
    }

    setIsLoadingMedicine(true);
    try {
      const requestData = {
        task: "getMedicineInfoFromURL",
        url: medicineUrl.trim()
      };
      
      const headers = { headers: { "Content-Type": "application/json" } };
      const response = await fetch(Api.PRESCRIPTION_DATA_FETCH, {
        method: "POST",
        headers: headers.headers,
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Log the full API response
      console.log("API Response:", data);
      console.log("Response type:", typeof data);
      
      // Extract the required parameters from the response (API returns a dictionary)
      const medicineInfo = {
        name: data.name || "N/A",
        genericname: data.genericname || "N/A",
        strength: data.strength || "N/A",
        medicinetype: data.medicinetype || "N/A",
        manufacturer: data.manufacturer || "N/A"
      };
      
      console.log("Extracted medicine info:", medicineInfo);
      setMedicineData(medicineInfo);
    } catch (error) {
      console.error("Error fetching medicine data:", error);
      alert("Error fetching medicine data. Please try again.");
    } finally {
      setIsLoadingMedicine(false);
    }
  };

  const handleAddNewMedicine = async () => {
    console.log("handleAddNewMedicine called");
    console.log("medicineData:", medicineData);
    console.log("medicineData type:", typeof medicineData);
    console.log("medicineData keys:", medicineData ? Object.keys(medicineData) : "null");
    
    if (medicineData) {
      try {
        // 1. Call the API to add medicine usage to database
        const requestData = {
          task: "addMedicneUsageToMedicineDBFromGenericName",
          drugname: medicineData.name,
          genericname: medicineData.genericname,
          strength: medicineData.strength,
          manufacturer: medicineData.manufacturer,
          medicinetype: medicineData.medicinetype
        };
        
        console.log("Making API call with requestData:", requestData);
        console.log("API endpoint:", Api.PRESCRIPTION_DATA_FETCH);
        
        const headers = { headers: { "Content-Type": "application/json" } };
        console.log("About to make fetch request...");
        const response = await fetch(Api.PRESCRIPTION_DATA_FETCH, {
          method: "POST",
          headers: headers.headers,
          body: JSON.stringify(requestData)
        });
        console.log("Fetch response received:", response);
        console.log("Response status:", response.status);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const apiResponse = await response.json();
        console.log("Medicine DB API Response:", apiResponse);

        // 2. If API returns a presigned URL, download and replace the file
        let downloadSuccess = false;
        if (apiResponse.presignedUrl) {
          try {
            // Download the updated file from presigned URL
            const fileResponse = await fetch(apiResponse.presignedUrl);
            if (!fileResponse.ok) {
              throw new Error(`Failed to download file: ${fileResponse.status}`);
            }
            
            const updatedMedicineData = await fileResponse.json();
            
            // Update the local state with the new medicine list
            setMedicineList(updatedMedicineData);
            
            // Save the file to public folder
            const blob = new Blob([JSON.stringify(updatedMedicineData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'allMedicines_dgda.json';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            console.log("Medicine file updated successfully from presigned URL");
            downloadSuccess = true;
          } catch (downloadError) {
            console.error("Error downloading updated file:", downloadError);
            throw new Error("Failed to download updated medicine file");
          }
        } else {
          throw new Error("No presigned URL received from API");
        }
        
        // 3. Close dialog and show success message only if download was successful
        if (downloadSuccess) {
          setShowNewMedicineDialog(false);
          setMedicineUrl("");
          setMedicineData(null);
          alert("Medicine added successfully to database!");
        }
      } catch (error) {
        console.error("Error adding medicine:", error);
        alert("Error adding medicine. Please try again.");
      }
    }
  };

  const handleRefreshMedicineList = async () => {
    try {
      // Call the API to get the presigned URL for medicine list
      const requestData = {
        task: "getMedicineListFilePresignedURL"
      };
      
      const headers = { headers: { "Content-Type": "application/json" } };
      const response = await fetch(Api.PRESCRIPTION_DATA_FETCH, {
        method: "POST",
        headers: headers.headers,
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();
      console.log("Medicine list API Response:", apiResponse);

      // Download the file from presigned URL
      if (apiResponse.presignedUrl) {
        const fileResponse = await fetch(apiResponse.presignedUrl);
        if (!fileResponse.ok) {
          throw new Error(`Failed to download file: ${fileResponse.status}`);
        }
        
        const updatedMedicineData = await fileResponse.json();
        
        // Update the local state with the new medicine list
        setMedicineList(updatedMedicineData);
        
        // Save the file to public folder
        const blob = new Blob([JSON.stringify(updatedMedicineData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'allMedicines_dgda.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log("Medicine list refreshed successfully");
        alert("Medicine list refreshed successfully!");
      } else {
        throw new Error("No presigned URL received from API");
      }
    } catch (error) {
      console.error("Error refreshing medicine list:", error);
      alert("Error refreshing medicine list. Please try again.");
    }
  };

  const formattedDate = () => {
    const now = new Date();
    const formatted = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
      now.getHours()
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
      now.getSeconds()
    ).padStart(2, "0")}`;
    return formatted;
  }

  const handleRunQA = async () => {
    setShowQADialog(true);
    setQaLoading(true);
    setQaError("");
    setQaResult(null);

    try {
      if (!patientInfo?.id) {
        throw new Error("Patient is not loaded yet.");
      }

      const presc_date = formattedDate();
      const prescription_dict = {
        complaints: complaints,
        on_exam: onExam,
        diagnosis: comments,
        advices: advices,
        pathology_tests: selectedPathology,
        radiology_tests: selectedRadiology,
        medicine: medicines,
        patient_name: patientInfo?.name,
        patient_age: patientInfo?.age,
        patient_gender: patientInfo?.gender,
        doctor_name: doctorName,
      };

      const requestData = {
        task: "getPrescriptionQA",
        patient_id: patientInfo.id,
        presc_date: presc_date,
        prescription_dict: prescription_dict,
      };
      console.log("QA API Request Data:", requestData);
      const headers = { headers: { "Content-Type": "application/json" } };
      const response = await fetch(Api.PRESCRIPTION_DATA_FETCH, {
        method: "POST",
        headers: headers.headers,
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      let data = await response.json();
      console.log("QA API Response Data:", data);

      // Handle wrapped or stringified response (e.g. Lambda body, or double-encoded JSON)
      if (data && typeof data.body === "string") {
        try {
          data = JSON.parse(data.body);
        } catch (_) {
          data = data;
        }
      } else if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (_) {
          data = {};
        }
      }
      // Unwrap if payload is under a 'data' key
      if (data && typeof data.data === "object" && data.data !== null) {
        data = data.data;
      }
      const qa = data || {};
      setQaResult({
        complaints: qa.complaints ?? "",
        medicine: qa.medicine ?? "",
        pathology_tests: qa.pathology_tests ?? "",
        radiology_tests: qa.radiology_tests ?? "",
        diagnosis: qa.diagnosis ?? "",
        quality_score: qa.quality_score ?? "",
      });
    } catch (e) {
      setQaError(e?.message || "Failed to run QA.");
    } finally {
      setQaLoading(false);
    }
  };

  const handleSave = async () => {
    const date = formattedDate();
    // await submitPrescription(prescriptionData);
    const prescriptionArray = [
      [1, appointmentId, complaints, comments, date, patientInfo.id],
    ];
    const medicinesArray = medicines.map((med) => [
      1,
      0,
      appointmentId,
      med.name,
      med.strength,
      med.dosage,
      med.timing,
      med.continueTill,
      patientInfo.id,
      date,
    ]);
    
    // Create prescription dictionary
    const prescription_dict = {
      complaints: complaints,
      on_exam: onExam,
      diagnosis: comments,
      advices: advices,
      pathology_tests: selectedPathology,
      radiology_tests: selectedRadiology,
      medicine: medicines,
      patient_name: patientInfo.name,
      patient_age: patientInfo.age,
      patient_gender: patientInfo.gender,
      doctor_name: doctorName,
    };
    
    try {
      const timeZone = "Asia/Dhaka";//Intl.DateTimeFormat().resolvedOptions().timeZone;
      await createAppointmentAndSubmitPrescription(doctorId, patientInfo.id, prescriptionArray, medicinesArray, timeZone, prescription_dict,appointmentId,date);
      // await updateServer("PRESCRIPTIONINFO", prescriptionArray);
      // await updateServer("MEDICINEPRECRIBEDTABLE", medicinesArray);
    } catch (error) {
      console.error("Error submitting prescription:", error);
    }
    // Optionally show a success message
    alert("Prescription submitted.");
  };

  const generatePDF = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString();

    const content = [
      { text: "Semion Limited", style: "clinic" },
      { text: "E-5, 167, Road 3, Mohakhali DOHS, Dhaka", style: "address" },
      {
        text: `Date: ${dateStr}   Time: ${timeStr}`,
        alignment: "right",
        margin: [0, 0, 0, 10],
      },
      {
        text: `Patient: ${patientInfo.name}   Age: ${patientInfo.age}   Gender: ${patientInfo.gender}`,
        margin: [0, 0, 0, 10],
      },
      { text: `Complaints: ${complaints}`, margin: [0, 0, 0, 5] },
      { text: `On Exam: ${onExam}`, margin: [0, 0, 0, 5] },
      { text: `Comments: ${comments}`, margin: [0, 0, 0, 5] },
      { text: `Advices: ${advices}`, margin: [0, 0, 0, 10] },
    ];

    if (selectedPathology.length > 0) {
      const pathologyBody = [
        ["Test Name", "Type"],
        ...selectedPathology.map((test) => [test[3], test[5]]),
      ];
      content.push({ text: "Pathology Tests", style: "subheader" });
      content.push({
        table: { headerRows: 1, widths: ["*", "*"], body: pathologyBody },
        layout: "lightHorizontalLines",
      });
    }

    if (selectedRadiology.length > 0) {
      const radiologyBody = [
        ["Test Name", "Type"],
        ...selectedRadiology.map((test) => [test[3], test[6]]),
      ];
      content.push({ text: "Radiology Tests", style: "subheader" });
      content.push({
        table: { headerRows: 1, widths: ["*", "*"], body: radiologyBody },
        layout: "lightHorizontalLines",
      });
    }

    if (medicines.length > 0) {
      content.push({
        text: "Medicines",
        style: "subheader",
        margin: [0, 10, 0, 5],
      });
      content.push({
        table: {
          headerRows: 1,
          widths: ["*", "*", "*", "*", "*", "*", "*"],
          body: [
            ["Name", "Strength", "Type", "Dosage", "Timing", "Duration", "Indication"],
            ...medicines.map((m) => [
              m.name,
              m.strength,
              m.type,
              m.dosage,
              m.timing,
              m.continueTill,
              m.indication,
            ]),
          ],
        },
        layout: "lightHorizontalLines",
      });
    }

    const docDefinition = {
      content,
      styles: {
        clinic: { fontSize: 18, bold: true, alignment: "center" },
        address: { fontSize: 12, alignment: "center", margin: [0, 0, 0, 10] },
        subheader: { fontSize: 14, bold: true, margin: [0, 10, 0, 4] },
      },
    };

    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    pdfDocGenerator.getBlob((blob) => {
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowPreview(true);
    });
  };

  const handlePrintPreview = () => {
    setShowPhotoPreview(true);
  };
  const handleClosePreview = () => {
    setShowPreview(false);
    setPdfUrl(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <Copilot />
      <div className="mx-auto">
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-2 px-2">
            Appointment
          </label>
          <select
            className="w-full px-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={appointmentId}
            onChange={(e) => setAppointmentId(e.target.value)}
          >
            {appointments && Array.isArray(appointments) && appointments.map((appt) => (
              <option key={appt.appointment_id} value={appt.appointment_id}>
                {appt.appointment_dt.split(" ")[0]}:{appt.appointment_id}
              </option>
            ))}
          </select>
        </div>

        {/* Clinical Information Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="text-green-600" size={24} />
            <p className="text-xl font-bold text-gray-800">
              Clinical Information
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Chief Complaints
              </label>
              <textarea
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="4"
                value={complaints}
                onChange={(e) => setComplaints(e.target.value)}
                placeholder="Enter patient complaints..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                On Exam
              </label>
              <textarea
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="4"
                value={onExam}
                onChange={(e) => setOnExam(e.target.value)}
                placeholder="Enter examination findings..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Diagnosis Comments
              </label>
              <textarea
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="4"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Enter physician notes..."
              />
            </div>
          </div>
        </div>

        {/* Tests Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div>
            <EnhancedTestSelector
              label="Pathology Tests"
              tests={pathologyList}
              onAdd={handleAddPatho}
              icon={TestTube}
            />
            <TestResultsTable
              tests={selectedPathology}
              onRemove={handleRemovePatho}
              title="Selected Pathology Tests"
              type="pathology"
            />
          </div>
          <div>
            <EnhancedTestSelector
              label="Radiology Tests"
              tests={radiologyList}
              onAdd={handleAddRadio}
              icon={Calendar}
            />
            <TestResultsTable
              tests={selectedRadiology}
              onRemove={handleRemoveRadio}
              title="Selected Radiology Tests"
              type="radiology"
            />
          </div>
        </div>

        {/* Medicines Section */}
        <div className="mb-8">
          {/* <EnhancedMedicineTable
            medicineList={medicineList}
            medicines={medicines}
            onAdd={handleAddMedicine}
            onRemove={handleRemoveMedicine}
          /> */}
          <MedicineSelector
            medicineList={medicineList}
            onAdd={handleAddMedicine}
          />
          <MedicineResults
            medicines={medicines}
            onRemove={handleRemoveMedicine}
          />
        </div>

        {/* Advices Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="text-orange-600" size={24} />
            <p className="text-xl font-bold text-gray-800">
              Advices
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Patient Advices
            </label>
            <textarea
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows="4"
              value={advices}
              onChange={(e) => setAdvices(e.target.value)}
              placeholder="Enter patient advices and instructions..."
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap gap-4">
            <button
              type="button"
              onClick={handlePrintPreview}
              className="flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <Eye size={18} className="mr-2" />
              Print Preview Prescription
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
            >
              <Save size={18} className="mr-2" />
              Save Prescription
            </button>
            <button
              type="button"
              onClick={() => setShowNewMedicineDialog(true)}
              className="flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors duration-200"
            >
              <Plus size={18} className="mr-2" />
              Add New Medicine
            </button>
            <button
              type="button"
              onClick={handleRefreshMedicineList}
              className="flex items-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            <button
              type="button"
              onClick={handleRunQA}
              className="ml-auto flex items-center px-6 py-3 bg-teal-600 text-white font-medium rounded-lg shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors duration-200"
            >
              Run QA
            </button>
          </div>
        </div>

      </div>
      
      {/* QA Dialog */}
      {showQADialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl p-6 shadow-xl relative max-h-[90%] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Prescription QA</h2>
              <button
                type="button"
                onClick={() => {
                  setShowQADialog(false);
                  setQaError("");
                  setQaResult(null);
                  setQaLoading(false);
                }}
                className="text-gray-600 hover:text-red-600"
              >
                <X size={24} />
              </button>
            </div>

            {qaLoading && (
              <div className="text-gray-700">Running QA...</div>
            )}

            {!qaLoading && qaError && (
              <div className="text-red-600 font-medium">{qaError}</div>
            )}

            {!qaLoading && !qaError && qaResult && (
              <div className="space-y-5">
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="text-sm text-teal-800 font-semibold">Quality Score</div>
                  <div className="text-2xl font-bold text-teal-900">{qaResult.quality_score}</div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">Complaints</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{qaResult.complaints}</div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">Medicine</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{qaResult.medicine}</div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">Pathology Tests</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{qaResult.pathology_tests}</div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">Radiology Tests</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{qaResult.radiology_tests}</div>
                </div>

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-1">Diagnosis</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{qaResult.diagnosis}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo Prescription Preview Modal */}
      {showPhotoPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[90%] p-4 shadow-xl relative overflow-y-auto">
            <button
              onClick={() => setShowPhotoPreview(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-red-600 z-10"
            >
              <X size={24} />
            </button>
            <PhotoPrescriptionPreviewUpdate8Oct25 
              prescription={{
                organization_name: "Semion Limited",
                creation_date: new Date().toISOString(),
                doctor_name: "Dr. " + (JSON.parse(localStorage.getItem("user_info") || "{}").name || "N/A"),
                name: patientInfo?.name || "N/A",
                age: patientInfo?.age || "N/A",
                cc_list: JSON.stringify({ complaints: complaints }),
                vitals: JSON.stringify({}),
                medicine_list: JSON.stringify(medicines.map(med => `${med.name} ${med.strength} (${med.type}) - ${med.dosage} ${med.timing} for ${med.continueTill} - Indication: ${med.indication}`)),
                advices_list: JSON.stringify({ diagnosis: comments, on_exam: onExam, advices: advices }),
                advices: advices, // Add advices directly to the prescription object
                followup: "",
                medicine_usage_list: JSON.stringify([]),
                organization_phone: "N/A",
                pathology_tests: selectedPathology,
                radiology_tests: selectedRadiology
              }}
            />
          </div>
        </div>
      )}

      {/* Add New Medicine Dialog */}
      {showNewMedicineDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-xl relative">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Add New Medicine</h2>
              <button
                onClick={() => {
                  setShowNewMedicineDialog(false);
                  setMedicineUrl("");
                  setMedicineData(null);
                }}
                className="text-gray-600 hover:text-red-600"
              >
                <X size={24} />
              </button>
            </div>

            {!medicineData ? (
              <div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Medicine URL
                  </label>
                  <input
                    type="url"
                    value={medicineUrl}
                    onChange={(e) => setMedicineUrl(e.target.value)}
                    placeholder="Enter medicine URL..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowNewMedicineDialog(false);
                      setMedicineUrl("");
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitMedicineUrl}
                    disabled={isLoadingMedicine || !medicineUrl.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingMedicine ? "Loading..." : "Submit"}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Medicine Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                      <p className="text-gray-800 font-medium">{medicineData.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Generic Name</label>
                      <p className="text-gray-800 font-medium">{medicineData.genericname}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Strength</label>
                      <p className="text-gray-800 font-medium">{medicineData.strength}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Medicine Type</label>
                      <p className="text-gray-800 font-medium">{medicineData.medicinetype}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Manufacturer</label>
                      <p className="text-gray-800 font-medium">{medicineData.manufacturer}</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setMedicineData(null);
                      setMedicineUrl("");
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleAddNewMedicine}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Add This Medicine
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
